/**
 * A purchase order line's side of the supplier interface (supplier interface
 * spec 09 §3, mock-up 11): the supplier's answer, its references and promised
 * date, a later date it proposed (*Accept new date* / *Move to another
 * supplier*), its proofs with our check (*Approve* / *Changes needed* while a
 * person decides; the file opens with the caller's auth), and *Copy file link*
 * once its artwork is released (asking first on a line sent through the
 * supplier's API, v1 completion M Q12). Its deliveries are `LineDespatches`.
 * Moving the line and asking for changes ask first. Every sentence is the
 * view's; a step is offered only where the view carries its URL.
 */
import { useState } from "react";
import type {
  DateChangeAcceptanceRequest,
  DecideSupplierProofRequest,
  PurchaseOrderLineView,
  SupplierProofView,
} from "@shared-core/data/order-to-invoice-types";
import { formatDateTimeOrDash } from "@shared-core/domain/formatters";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../primitives/alert-dialog";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import { Label } from "../../primitives/label";
import { Textarea } from "../../primitives/textarea";
import { SupplierFileLink } from "./SupplierFileLink";

const ACKNOWLEDGEMENT_TONE = {
  waiting: "warning",
  accepted: "success",
  date_proposed: "secondary",
  cancel_requested: "warning",
  rejected: "error",
} as const;

const CHECK_TONE = {
  not_required: "neutral",
  queued: "neutral",
  passed: "success",
  concern: "warning",
  failed_to_run: "warning",
} as const;

/** True when the line has anything of the supplier interface to show. */
export function hasSupplierSide(line: PurchaseOrderLineView): boolean {
  return (
    !!line.acknowledgement ||
    line.supplier_references.length > 0 ||
    !!line.supplier_quote_reference ||
    !!line.expected ||
    !!line.date_proposal ||
    line.proofs.length > 0 ||
    !!line.issue_file_link_url
  );
}

export function PurchaseOrderLineSupplier({
  line,
  supplier,
  isBusy,
  onDecideDate,
  onDecideProof,
  onOpenProof,
}: {
  line: PurchaseOrderLineView;
  supplier: string;
  isBusy: boolean;
  onDecideDate: (input: DateChangeAcceptanceRequest) => void;
  onDecideProof: (
    proof: SupplierProofView,
    input: DecideSupplierProofRequest,
  ) => void;
  onOpenProof: (proof: SupplierProofView) => void;
}) {
  const [isMoving, setIsMoving] = useState(false);
  const [askingChangesFor, setAskingChangesFor] =
    useState<SupplierProofView | null>(null);
  const [note, setNote] = useState("");
  const proposal = line.date_proposal;
  return (
    <div
      className="space-y-2 text-sm"
      aria-label={`Line ${line.line_no} with the supplier`}
    >
      <div className="flex flex-wrap items-center gap-2">
        {line.acknowledgement && (
          <Badge variant={ACKNOWLEDGEMENT_TONE[line.acknowledgement.state]}>
            {line.acknowledgement.label}
          </Badge>
        )}
        {line.supplier_references.length > 0 && (
          <span className="text-muted-foreground">
            Their ref {line.supplier_references.join(", ")}
          </span>
        )}
        {line.supplier_quote_reference && (
          <span className="text-muted-foreground">
            Sent with their quote reference {line.supplier_quote_reference}
          </span>
        )}
        {line.expected && (
          <span
            className={
              line.expected.confidence === "unknown"
                ? "text-amber-700"
                : "text-muted-foreground"
            }
          >
            {line.expected.label
              ? `${line.expected.label} · ${line.expected.note}`
              : line.expected.note}
          </span>
        )}
      </div>

      {proposal && (
        <div className="flex flex-wrap items-center gap-2 rounded-md bg-muted/50 px-3 py-2">
          <span>{proposal.sentence}</span>
          {proposal.decide_url && (
            <>
              <Button
                size="sm"
                disabled={isBusy}
                onClick={() => onDecideDate({ outcome: "accepted" })}
              >
                Accept new date
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={isBusy}
                onClick={() => setIsMoving(true)}
              >
                Move to another supplier
              </Button>
            </>
          )}
        </div>
      )}

      {line.proofs.length > 0 && (
        <ul
          className="space-y-1.5"
          aria-label={`Proofs for line ${line.line_no}`}
        >
          {line.proofs.map((proof) => (
            <li key={proof.id} className="flex flex-wrap items-center gap-2">
              <span
                className={proof.is_superseded ? "line-through" : undefined}
              >
                Proof{" "}
                <button
                  type="button"
                  className="text-[color:var(--color-primary)] underline"
                  onClick={() => onOpenProof(proof)}
                >
                  {proof.file_name}
                </button>{" "}
                <span className="text-muted-foreground">
                  from {proof.uploaded_by},{" "}
                  {formatDateTimeOrDash(proof.uploaded_at)}
                </span>
              </span>
              <Badge variant={CHECK_TONE[proof.check_state]}>
                {proof.check_label}
              </Badge>
              {proof.reasons.length > 0 && (
                <span className="text-muted-foreground">
                  {proof.reasons.join(", ")}
                </span>
              )}
              {proof.decision_label && (
                <span className="font-medium">{proof.decision_label}</span>
              )}
              {proof.note && (
                <span className="text-muted-foreground">“{proof.note}”</span>
              )}
              {proof.decide_url && (
                <>
                  <Button
                    size="sm"
                    disabled={isBusy}
                    onClick={() =>
                      onDecideProof(proof, { outcome: "approved", note: null })
                    }
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={isBusy}
                    onClick={() => {
                      setNote("");
                      setAskingChangesFor(proof);
                    }}
                  >
                    Changes needed
                  </Button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      <SupplierFileLink
        line={line}
        supplier={supplier}
        confirmation={line.file_link_confirmation}
      />

      <AlertDialog open={isMoving} onOpenChange={setIsMoving}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Move line {line.line_no} to another supplier?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The new date is declined and the line is cancelled with this
              supplier. It returns to To place, where you can choose another
              supplier.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it here</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setIsMoving(false);
                onDecideDate({ outcome: "declined" });
              }}
            >
              Move to another supplier
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={askingChangesFor !== null}
        onOpenChange={(open) => !open && setAskingChangesFor(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ask for changes to the proof</AlertDialogTitle>
            <AlertDialogDescription>
              The supplier is told what to change and uploads a new proof.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor={`proof-note-${line.line_no}`}>What to change</Label>
            <p
              id={`proof-note-${line.line_no}-hint`}
              className="text-xs text-muted-foreground"
            >
              The supplier sees this
            </p>
            <Textarea
              aria-describedby={`proof-note-${line.line_no}-hint`}
              id={`proof-note-${line.line_no}`}
              value={note}
              maxLength={500}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!note.trim()}
              onClick={() => {
                const proof = askingChangesFor;
                setAskingChangesFor(null);
                if (proof)
                  onDecideProof(proof, {
                    outcome: "changes_needed",
                    note: note.trim(),
                  });
              }}
            >
              Ask for changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
